// TermsSettings.js
// Settings > Terms & Signatures. Edits the customer-facing terms a customer
// agrees to when signing for a job. Owners / sOW / RM / Org 1 admins pick a
// scope (Global or one service provider); SP owners and Org 2 admins are
// pinned to their own vendor. Resolution per job: vendor+service ->
// vendor shared -> global+service -> global shared -> built-in default.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ViewOnlyNotice from './ViewOnlyNotice';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Autocomplete, Stack
} from '@mui/material';
import { useUser } from '../context/UserContext';
import { useNotification } from '../context/NotificationContext';

const API = process.env.REACT_APP_API_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function TermsSettings({ readOnly = false }) {
  const { currentUser } = useUser();
  const { showNotification } = useNotification();

  const canChooseScope = useMemo(() => {
    if (!currentUser) return false;
    return ['OW', 'sOW', 'OWp', 'RM'].includes(currentUser.primaryRole) ||
      (currentUser.organizationLevel === 1 && currentUser.secondaryRoles?.admin === true);
  }, [currentUser]);

  const [scopeMode, setScopeMode] = useState('global'); // 'global' | 'vendor'
  const [vendor, setVendor] = useState(null); // { vendorNumber, label }
  const [spOptions, setSpOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null); // { scope, services, terms, defaults }

  // Shared ('*') editor
  const [shared, setShared] = useState({ title: '', body: '' });
  const [sharedSaving, setSharedSaving] = useState(false);

  // Per-service dialog
  const [dialog, setDialog] = useState(null); // { serviceKey, title, body, isCustom, loading }
  const [dialogSaving, setDialogSaving] = useState(false);

  // SP list for the scope picker
  useEffect(() => {
    if (!canChooseScope) return;
    (async () => {
      try {
        const { data: res } = await axios.get(`${API}/api/users?role=SP&fetchForAllVendors=true&limit=500`, { headers: getAuthHeader() });
        const users = res?.records || [];
        const seen = new Set();
        const options = users
          .filter((u) => u.vendorNumber || u.vendorId)
          .map((u) => ({
            vendorNumber: u.vendorNumber || u.vendorId,
            label: `${u.companyName || `${u.firstName || ''} ${u.lastName || ''}`.trim()} (${u.vendorNumber || u.vendorId})`
          }))
          .filter((o) => !seen.has(o.vendorNumber) && seen.add(o.vendorNumber))
          .sort((a, b) => a.label.localeCompare(b.label));
        setSpOptions(options);
      } catch {
        // Picker degrades to free text
      }
    })();
  }, [canChooseScope]);

  // The vendorNumber query we send; null => global. Pinned users send nothing.
  const scopeParam = canChooseScope ? (scopeMode === 'vendor' ? vendor?.vendorNumber || null : 'global') : null;

  const customFor = useCallback((serviceKey) => (data?.terms || []).find((t) => t.serviceKey === serviceKey) || null, [data]);

  const fetchPreview = useCallback(async (serviceKey) => {
    const params = new URLSearchParams();
    if (scopeParam) params.set('vendorNumber', scopeParam);
    params.set('serviceKey', serviceKey);
    const { data: res } = await axios.get(`${API}/api/service-terms/preview?${params}`, { headers: getAuthHeader() });
    return res.terms;
  }, [scopeParam]);

  const load = useCallback(async () => {
    if (canChooseScope && scopeMode === 'vendor' && !vendor) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const params = scopeParam ? `?vendorNumber=${encodeURIComponent(scopeParam)}` : '';
      const { data: res } = await axios.get(`${API}/api/service-terms${params}`, { headers: getAuthHeader() });
      setData(res);
      const custom = (res.terms || []).find((t) => t.serviceKey === '*');
      if (custom) {
        setShared({ title: custom.title, body: custom.body });
      } else {
        // Start from what currently applies (global shared for a vendor scope, else the built-in text)
        const preview = await (async () => {
          const p = new URLSearchParams();
          if (scopeParam) p.set('vendorNumber', scopeParam);
          p.set('serviceKey', '*');
          const r = await axios.get(`${API}/api/service-terms/preview?${p}`, { headers: getAuthHeader() });
          return r.data.terms;
        })();
        setShared({ title: preview.title, body: preview.body });
      }
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Failed to load service terms.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canChooseScope, scopeMode, vendor, scopeParam]);

  useEffect(() => { load(); }, [load]);

  const putTerms = async (serviceKey, title, body) => {
    const payload = { vendorNumber: scopeParam === 'global' ? null : scopeParam, serviceKey, title, body };
    const { data: res } = await axios.put(`${API}/api/service-terms`, payload, { headers: getAuthHeader() });
    return res.terms;
  };

  const deleteTerms = async (serviceKey) => {
    const params = new URLSearchParams();
    if (scopeParam) params.set('vendorNumber', scopeParam);
    params.set('serviceKey', serviceKey);
    await axios.delete(`${API}/api/service-terms?${params}`, { headers: getAuthHeader() });
  };

  const saveShared = async () => {
    setSharedSaving(true);
    try {
      await putTerms('*', shared.title, shared.body);
      showNotification('Shared terms saved', 'success');
      await load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save shared terms', 'error');
    } finally {
      setSharedSaving(false);
    }
  };

  const resetShared = async () => {
    setSharedSaving(true);
    try {
      await deleteTerms('*');
      showNotification('Shared terms reset', 'success');
      await load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to reset shared terms', 'error');
    } finally {
      setSharedSaving(false);
    }
  };

  const openEdit = async (serviceKey) => {
    const custom = customFor(serviceKey);
    if (custom) {
      setDialog({ serviceKey, title: custom.title, body: custom.body, isCustom: true, loading: false });
      return;
    }
    setDialog({ serviceKey, title: '', body: '', isCustom: false, loading: true });
    try {
      const preview = await fetchPreview(serviceKey);
      setDialog((d) => d && d.serviceKey === serviceKey ? { ...d, title: preview.title, body: preview.body, loading: false } : d);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to load terms preview', 'error');
      setDialog(null);
    }
  };

  const saveDialog = async () => {
    if (!dialog) return;
    setDialogSaving(true);
    try {
      await putTerms(dialog.serviceKey, dialog.title, dialog.body);
      showNotification(`Terms saved for ${dialog.serviceKey}`, 'success');
      setDialog(null);
      await load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save terms', 'error');
    } finally {
      setDialogSaving(false);
    }
  };

  const resetDialog = async () => {
    if (!dialog) return;
    setDialogSaving(true);
    try {
      await deleteTerms(dialog.serviceKey);
      showNotification(`${dialog.serviceKey} now uses the shared terms`, 'success');
      setDialog(null);
      await load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to reset terms', 'error');
    } finally {
      setDialogSaving(false);
    }
  };

  const sharedCustom = customFor('*');
  const scopeLabel = canChooseScope
    ? (scopeMode === 'vendor' ? (vendor ? vendor.label : 'a service provider') : 'all providers (global)')
    : (currentUser?.companyName || 'your company');

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Terms & Signatures</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These are the terms a customer reads and agrees to when signing for a job, on the driver's device
        or from a texted/emailed link. Each service uses its own custom terms if set, otherwise the shared terms.
        {canChooseScope && ' A service provider\'s terms override the global ones for jobs they service.'}
      </Typography>

      {canChooseScope && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={scopeMode}
              onChange={(e, v) => { if (v) setScopeMode(v); }}
            >
              <ToggleButton value="global">Global</ToggleButton>
              <ToggleButton value="vendor">Service provider</ToggleButton>
            </ToggleButtonGroup>
            {scopeMode === 'vendor' && (
              <Autocomplete
                size="small"
                sx={{ minWidth: 320, flex: 1 }}
                options={spOptions}
                value={vendor}
                onChange={(e, v) => setVendor(v)}
                isOptionEqualToValue={(a, b) => a.vendorNumber === b.vendorNumber}
                renderInput={(params) => <TextField {...params} label="Service provider" placeholder="Choose a provider" />}
              />
            )}
          </Stack>
        </Paper>
      )}

      {readOnly && <ViewOnlyNotice />}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : !data ? (
        canChooseScope && scopeMode === 'vendor' && !vendor
          ? <Alert severity="info">Choose a service provider to edit their terms.</Alert>
          : null
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6">Shared terms (all services) — {scopeLabel}</Typography>
              <Chip size="small" label={sharedCustom ? `Custom · v${sharedCustom.version}` : (canChooseScope && scopeMode === 'vendor' ? 'Inherited from global' : 'Built-in default')} color={sharedCustom ? 'primary' : 'default'} />
            </Box>
            <TextField
              label="Title"
              disabled={readOnly}
              value={shared.title}
              onChange={(e) => setShared((s) => ({ ...s, title: e.target.value }))}
              fullWidth
              margin="dense"
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Terms"
              disabled={readOnly}
              value={shared.body}
              onChange={(e) => setShared((s) => ({ ...s, body: e.target.value }))}
              fullWidth
              multiline
              minRows={10}
              maxRows={24}
              margin="dense"
              helperText="Plain text. Blank lines separate paragraphs."
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {!readOnly && <Button variant="contained" onClick={saveShared} disabled={sharedSaving || !shared.title.trim() || !shared.body.trim()}>Save shared terms</Button>}
              {!readOnly && sharedCustom && <Button color="warning" onClick={resetShared} disabled={sharedSaving}>Reset to default</Button>}
              {sharedSaving && <CircularProgress size={22} sx={{ mt: 0.75 }} />}
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Terms by service</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Terms</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.services || []).map((service) => {
                  const custom = customFor(service);
                  return (
                    <TableRow key={service} hover>
                      <TableCell>{service}</TableCell>
                      <TableCell>
                        <Chip size="small" label={custom ? `Custom · v${custom.version}` : 'Shared'} color={custom ? 'primary' : 'default'} variant={custom ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell sx={{ color: custom ? 'text.primary' : 'text.secondary' }}>
                        {custom ? custom.title : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {!readOnly && <Button size="small" onClick={() => openEdit(service)}>Edit</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={!!dialog} onClose={dialogSaving ? undefined : () => setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>{dialog?.serviceKey} — {dialog?.isCustom ? 'custom terms' : 'custom terms (starting from what applies now)'}</DialogTitle>
        <DialogContent dividers>
          {dialog?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : dialog && (
            <>
              <TextField
                label="Title"
                value={dialog.title}
                onChange={(e) => setDialog((d) => ({ ...d, title: e.target.value }))}
                fullWidth
                margin="dense"
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                label="Terms"
                value={dialog.body}
                onChange={(e) => setDialog((d) => ({ ...d, body: e.target.value }))}
                fullWidth
                multiline
                minRows={14}
                maxRows={30}
                margin="dense"
                helperText="Plain text. Blank lines separate paragraphs."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {dialogSaving && <CircularProgress size={20} sx={{ mr: 1 }} />}
          <Button onClick={() => setDialog(null)} disabled={dialogSaving}>Cancel</Button>
          {dialog?.isCustom && <Button color="warning" onClick={resetDialog} disabled={dialogSaving}>Reset to shared</Button>}
          <Button variant="contained" onClick={saveDialog} disabled={dialogSaving || dialog?.loading || !dialog?.title?.trim() || !dialog?.body?.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TermsSettings;
